import { Style, Stroke, Text, Fill } from 'ol/style';
import { DEFAULT_CONTOUR_COLOR } from '@config/contoursLayerConfig';

/**
 * Creates a style function for contour lines with major/minor differentiation and labels
 *
 * @param {Object} styling - Styling configuration object
 * @param {number} styling.line_width - Width for minor contour lines
 * @param {number} styling.major_line_width - Width for major contour lines
 * @param {number} contourInterval - Interval between contour lines in meters
 * @param {Object} colorRgba - Color object with r, g, b, a properties
 * @returns {Function} OpenLayers style function
 */
export const createContourStyleFunction = (
	styling = {},
	contourInterval = 1,
	colorRgba = DEFAULT_CONTOUR_COLOR
) => {
	const lineColor = `rgba(${colorRgba.r}, ${colorRgba.g}, ${colorRgba.b}, ${colorRgba.a})`;
	const lineWidth = styling.line_width || 1;
	const majorLineWidth = styling.major_line_width || 2;

	// Determine major and label intervals based on contour interval
	let majorInterval, labelInterval;

	if (contourInterval >= 2) {
		labelInterval = contourInterval;
		majorInterval = contourInterval * 2;
	} else if (contourInterval >= 1) {
		labelInterval = 2;
		majorInterval = 5;
	} else {
		labelInterval = 2;
		majorInterval = 5;
	}

	// Cache styles for performance
	const styleCache = {};

	return (feature, resolution) => {
		const level = feature.get('level');

		// Fallback style if level is not defined
		if (level === undefined || level === null) {
			return new Style({
				stroke: new Stroke({ color: lineColor, width: lineWidth }),
			});
		}

		const isMajor = Math.abs(level % majorInterval) < 0.01;
		const showLabel = Math.abs(level % labelInterval) < 0.01;

		// Create cache key based on level and resolution tier
		const resolutionKey = resolution < 0.5 ? 'high' : resolution < 2 ? 'med' : 'low';
		const cacheKey = `${level}-${resolutionKey}`;

		// Return cached style if available
		if (styleCache[cacheKey]) {
			return styleCache[cacheKey];
		}

		const styles = [];

		// Add stroke style
		styles.push(
			new Style({
				stroke: new Stroke({
					color: lineColor,
					width: isMajor ? majorLineWidth : lineWidth,
				}),
			})
		);

		// Add text label for major intervals at appropriate zoom levels
		if (showLabel && resolution < 2) {
			styles.push(
				new Style({
					text: new Text({
						text: `${level}m`,
						font: isMajor ? 'bold 12px sans-serif' : '11px sans-serif',
						placement: 'line',
						textBaseline: 'bottom',
						offsetY: -2,
						fill: new Fill({ color: lineColor }),
						stroke: new Stroke({ color: 'rgba(255, 255, 255, 0.9)', width: 3 }),
						overflow: true,
						maxAngle: Math.PI / 6,
					}),
				})
			);
		}

		// Cache and return the style
		styleCache[cacheKey] = styles;
		return styles;
	};
};
